// Validation utility functions

/**
 * Validates if a value is empty
 * @param {*} value - Value to check
 * @returns {boolean} True if value is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Validates if a value meets required criteria
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateRequired = (value, fieldName) => {
  function toTitleCase(str) {
    return str
      .replace(/([A-Z])/g, ' $1')     // Add space before capital letters
      .replace(/^./, s => s.toUpperCase()); // Capitalize first letter
  }
  return isEmpty(value) ? `${toTitleCase(fieldName)} is required` : null;
};

/**
 * Validates phone number format
 * @param {string} phone - Phone number to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePhone = (phone) => {
  if (isEmpty(phone)) return null; // Skip if empty (use validateRequired for required fields)
  // Allows +, spaces, hyphens, and parentheses, requires 8-20 digits
  const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
  return phoneRegex.test(phone) ? null : 'Invalid phone number format';
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateEmail = (email) => {
  if (isEmpty(email)) return null;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) ? null : 'Invalid email format';
};

/**
 * Validates website URL format
 * @param {string} website - Website URL to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateWebsite = (website) => {
  if (isEmpty(website)) return null;
  const websiteRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
  return websiteRegex.test(website) ? null : 'Invalid website format';
};

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecial = true
  } = options;

  if (isEmpty(password)) return null;

  const errors = [];

  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters`);
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return errors.length > 0 ? errors.join(', ') : null;
};

/**
 * Validates if two passwords match
 * @param {string} password - Password
 * @param {string} confirmPassword - Password confirmation
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  if (isEmpty(password) || isEmpty(confirmPassword)) return null;
  return password === confirmPassword ? null : 'Passwords do not match';
};

/**
 * Validates postal code format
 * @param {string} postalCode - Postal code to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePostalCode = (postalCode) => {
  if (isEmpty(postalCode)) return null;
  // Allows 5-10 digits, removing any spaces
  return /^[0-9]{5,10}$/.test(postalCode.replace(/\s/g, '')) ? null : 'Invalid postal code format';
};

/**
 * Validates text length
 * @param {string} text - Text to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateLength = (text, options = {}) => {
  const { min, max, fieldName = 'Text' } = options;
  
  if (isEmpty(text)) return null;
  
  if (min && text.length < min) {
    return `${fieldName} must be at least ${min} characters`;
  }
  
  if (max && text.length > max) {
    return `${fieldName} must not exceed ${max} characters`;
  }
  
  return null;
};

/**
 * Validates a form field based on its type and requirements
 * @param {string} fieldName - Name of the field
 * @param {*} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateField = (fieldName, value, options = {}) => {
  const {
    required = false,
    type = 'text',
    minLength,
    maxLength,
    passwordOptions,
    confirmPassword
  } = options;

  // Check required first
  if (required) {
    const requiredError = validateRequired(value, fieldName);
    if (requiredError) return requiredError;
  }

  // Skip other validations if empty and not required
  if (isEmpty(value) && !required) return null;

  // Validate based on type
  switch (type) {
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'website':
      return validateWebsite(value);
    case 'password':
      const passwordError = validatePassword(value, passwordOptions);
      if (passwordError) return passwordError;
      if (confirmPassword) {
        return validatePasswordMatch(value, confirmPassword);
      }
      return null;
    case 'postalCode':
      return validatePostalCode(value);
    case 'text':
    default:
      if (minLength || maxLength) {
        return validateLength(value, { min: minLength, max: maxLength, fieldName });
      }
      return null;
  }
}; 